import React, { useState } from 'react';
import { Box, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import TrainOutlinedIcon from '@mui/icons-material/TrainOutlined';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
} from 'recharts';

import { I18nProvider, useI18n } from './i18n/I18nContext';
import { LINES } from './analysis/network';
import HomeNav from './components/HomeNav';
import { HOME_PALETTE as P } from './homeTheme';
import { useHomeLive } from './home/useHomeLive';
import { toAccuracyNode } from './analysis/accuracyFromFirestore';
import { primaryHitRate } from './analysis/accuracyApi';

/**
 * Landing page at /home. LAYOUT AND STYLE REVIEW ONLY — every number below is
 * a placeholder from the design reference, deliberately not wired to Firestore
 * or the analysis API. Replace DEMO with real sources once the layout is agreed;
 * nothing outside DEMO holds a value.
 *
 * Note that four tiles (users, alarm types, all alarms, error log) have no data
 * source anywhere in the platform today. They are drawn because they are in the
 * reference design; they need a source or removal before wiring.
 */

type Level = 'high' | 'medium' | 'low';

interface DetectedEvent {
  id: number;
  name: string;
  venue: string;
  when: string;
  impact: Level;
  confidence: Level;
}

/** One stop on the impact corridor. `target` marks the stop serving the venue. */
interface ImpactStop {
  name: string;
  level: Level;
  target?: boolean;
}

interface ImpactCorridor {
  line: string;
  stops: ImpactStop[];
  /** Demo figure: peak occupancy above forecast at the worst stop. */
  peakPct: number;
}

/**
 * The headline figures for a scope (all lines, or one line).
 *
 * Every field is nullable now that the source is real. The placeholder could
 * always supply a number; the rollup cannot — a horizon with no samples, or a
 * day where the BigQuery model archive was unreachable, has genuinely no value,
 * and rendering 0% there would be a lie rather than a gap.
 */
interface AccuracyMetrics {
  horizons: { key: string; hit: number | null }[];
  overall: number | null;
  samples: number | null;
  /** HIGH read as LOW, as a percentage of actual-HIGH samples. */
  hnMiss: number | null;
  nHigh: number | null;
  maePersons: number | null;
}

/** Fixed order, so a missing horizon renders as a gap rather than shifting the row. */
const ACC_HORIZONS = ['N+1', 'N+2', 'N+3', 'N+4', 'N+5'] as const;

const DEMO = {
  // paxWeek/paxMonth/paxYear and `year` retired 2026-09-12. The ladder is now
  // Day / 7 days / 30 days from trips_day + trips_rolling. No annual tile: AFZ
  // coverage is not good enough to publish one, and history starts 2026-04-17.
  // trainsLive: retired — the tile now reads aggregates/home_live. Kept out of
  // DEMO so nothing can silently fall back to a placeholder.
  // paxDay: retired 2026-09-12 — the Day tile reads trips_day.
  trend: [
    { year: 2025, pct: 105, delta: 5 },
    { year: 2026, pct: 120, delta: 15 },
  ],
  // currentPax: retired 2026-09-11 — the Current passengers box now reads
  // aggregates/home_live.passengers_onboard. Kept out of DEMO so nothing can
  // silently fall back to placeholder figures.
  // FzO-BO is retired from DEMO deliberately — it now reads
  // aggregates/home_live.feeds, sourced from the listener's own counters via
  // Cloud Monitoring, and must never be able to fall back to invented volumes.
  //
  // RIS:MGW is also retired from DEMO as of 2026-09-15 — it is measured at the
  // topic (pubsub.googleapis.com/topic/send_message_operation_count on
  // ris-feed) rather than in its listener, which does no filtering.
  //
  // The remaining three have no counter in their listeners yet. They stay as
  // PLACEHOLDERS, rendered grey, because the box is a checklist of feeds as
  // much as a set of numbers: omitting a feed reads as "no such feed" rather
  // than "not measured yet". Delete each row as its listener is instrumented.
  //
  // Treat the magnitudes with suspicion — the FzO-BO placeholder was ~4x
  // reality (78k/hour invented against ~24k measured), so these are probably
  // high too.
  feedPlaceholders: {
    hour: [
      { name: 'RIS:JOURNEYS', received: 13450, errors:   4 },
      { name: 'AFZ',          received: 21900, errors: 310 },
      { name: 'WIFI',         received: 18600, errors:   3 },
    ],
    day: [
      { name: 'RIS:JOURNEYS', received: 214880, errors:   96 },
      { name: 'AFZ',          received: 342150, errors: 2470 },
      { name: 'WIFI',         received: 298740, errors:   51 },
    ],
  },
  messagesAll: 45,
  messagesActive: 32,
  messagesActiveDelta: 24,
  /**
   * Detected external events. Placeholder rows for the layout review: real
   * venues on the S-Bahn network, invented dates, and impact/confidence made
   * up rather than scored. Performer and production names are stand-ins for
   * whatever the detector actually returns.
   */
  events: ([
    { id: 1, name: 'Felix Lobrecht — Show',        venue: 'Hanns-Martin-Schleyer-Halle, Bad Cannstatt', when: 'Sa 22.08. 20:00', impact: 'high',   confidence: 'high'   },
    { id: 2, name: 'VfB Stuttgart — Heimspiel',    venue: 'MHPArena, Bad Cannstatt',                    when: 'So 23.08. 15:30', impact: 'high',   confidence: 'high'   },
    { id: 3, name: 'Cannstatter Volksfest — Eröffnung', venue: 'Cannstatter Wasen',                     when: 'Fr 26.09. 16:00', impact: 'high',   confidence: 'medium' },
    { id: 4, name: 'Holiday on Ice',               venue: 'Porsche-Arena, Bad Cannstatt',               when: 'Fr 21.08. 19:00', impact: 'medium', confidence: 'high'   },
    { id: 5, name: 'Stuttgarter Ballett — Schwanensee', venue: 'Opernhaus Stuttgart',                   when: 'Do 20.08. 19:30', impact: 'medium', confidence: 'high'   },
    { id: 6, name: 'Tanz der Vampire — Musical',   venue: 'Palladium Theater, SI-Centrum Möhringen',    when: 'täglich 19:30',    impact: 'medium', confidence: 'medium' },
    { id: 7, name: 'Comedy Night',                 venue: 'Theaterhaus Stuttgart, Pragsattel',          when: 'Sa 22.08. 20:00', impact: 'medium', confidence: 'medium' },
    { id: 8, name: 'Riverdance — Tour',            venue: 'Beethovensaal, Liederhalle',                 when: 'Mi 26.08. 19:30', impact: 'medium', confidence: 'low'    },
    { id: 9, name: 'Ludwigsburger Schlossfestspiele', venue: 'Forum am Schlosspark, Ludwigsburg',        when: 'Sa 29.08. 18:00', impact: 'low',    confidence: 'medium' },
    { id: 10, name: 'Fellbacher Herbst',           venue: 'Schwabenlandhalle, Fellbach',                when: 'So 04.10. 11:00', impact: 'low',    confidence: 'high'   },
    { id: 11, name: 'Tango Argentino — Gala',      venue: 'Filderhalle, Leinfelden-Echterdingen',       when: 'Fr 28.08. 20:00', impact: 'low',    confidence: 'low'    },
  ] as DetectedEvent[]),
  /**
   * Passenger-information (PIS) capacity messages actually published, against
   * the stops that should have received one. 100% is the target; feed gaps pull
   * individual lines down, so the per-line split is the point of this tile
   * rather than the headline number. Totals are summed from the rows.
   */
  // pis: retired 2026-09-13 — the box reads vu_coverage from
  // aggregates/home_live.
  /**
   * Forecast-quality summary — the headline figures from AccuracyBox on
   * /analysis/messages, pooled and per line. Real shape, invented numbers:
   * hit rate per horizon, pooled hit rate, sample count, the HIGH-read-as-LOW
   * miss rate and mean absolute error in people.
   */
  // RETIRED 2026-09-17 — the card now reads aggregates/home_live.accuracy,
  // written hourly by the accuracy rollup. Commented out rather than deleted,
  // per the practice established with trainsLive/currentPax/pis: a wired tile
  // must not be able to fall back to an invented figure. Delete once the
  // Firestore path has been live for a while.
  //
  // The placeholder magnitudes here were plausible but unverified; the real
  // day-one reading was 92.6% over 113,188 samples for 2026-09-16.
  _retired_forecast: {
    all: {
      horizons: [
        { key: 'N+1', hit: 94 }, { key: 'N+2', hit: 91 }, { key: 'N+3', hit: 86 },
        { key: 'N+4', hit: 78 }, { key: 'N+5', hit: 71 },
      ],
      overall: 88, samples: 12480, hnMiss: 3.2, nHigh: 1842, maePersons: 11,
    },
    byLine: {
      S1: {
        horizons: [
          { key: 'N+1', hit: 93 }, { key: 'N+2', hit: 90 }, { key: 'N+3', hit: 84 },
          { key: 'N+4', hit: 76 }, { key: 'N+5', hit: 69 },
        ],
        overall: 86, samples: 3120, hnMiss: 4.1, nHigh: 612, maePersons: 14,
      },
      S2: {
        horizons: [
          { key: 'N+1', hit: 92 }, { key: 'N+2', hit: 89 }, { key: 'N+3', hit: 85 },
          { key: 'N+4', hit: 77 }, { key: 'N+5', hit: 70 },
        ],
        overall: 85, samples: 2870, hnMiss: 5.4, nHigh: 548, maePersons: 13,
      },
      S3: {
        horizons: [
          { key: 'N+1', hit: 95 }, { key: 'N+2', hit: 92 }, { key: 'N+3', hit: 88 },
          { key: 'N+4', hit: 80 }, { key: 'N+5', hit: 74 },
        ],
        overall: 89, samples: 2140, hnMiss: 2.6, nHigh: 331, maePersons: 10,
      },
      S4: {
        horizons: [
          { key: 'N+1', hit: 96 }, { key: 'N+2', hit: 94 }, { key: 'N+3', hit: 90 },
          { key: 'N+4', hit: 84 }, { key: 'N+5', hit: 78 },
        ],
        overall: 92, samples: 1490, hnMiss: 1.8, nHigh: 176, maePersons: 8,
      },
      S5: {
        horizons: [
          { key: 'N+1', hit: 95 }, { key: 'N+2', hit: 93 }, { key: 'N+3', hit: 89 },
          { key: 'N+4', hit: 83 }, { key: 'N+5', hit: 77 },
        ],
        overall: 91, samples: 1180, hnMiss: 2.1, nHigh: 143, maePersons: 9,
      },
      S6: {
        horizons: [
          { key: 'N+1', hit: 94 }, { key: 'N+2', hit: 90 }, { key: 'N+3', hit: 86 },
          { key: 'N+4', hit: 79 }, { key: 'N+5', hit: 72 },
        ],
        overall: 88, samples: 1060, hnMiss: 3.0, nHigh: 128, maePersons: 10,
      },
      S60: {
        horizons: [
          { key: 'N+1', hit: 88 }, { key: 'N+2', hit: 84 }, { key: 'N+3', hit: 78 },
          { key: 'N+4', hit: 69 }, { key: 'N+5', hit: 62 },
        ],
        overall: 79, samples: 420, hnMiss: 8.7, nHigh: 54, maePersons: 16,
      },
      S62: {
        horizons: [
          { key: 'N+1', hit: 86 }, { key: 'N+2', hit: 81 }, { key: 'N+3', hit: 74 },
          { key: 'N+4', hit: 66 }, { key: 'N+5', hit: 58 },
        ],
        overall: 76, samples: 200, hnMiss: 11.4, nHigh: 26, maePersons: 18,
      },
    } as Record<string, AccuracyMetrics>,
  },
  capacity: [
    { occupied: 85000, free: 15000 },
    { occupied: 22000, free: 78000 },
    { occupied: 68000, free: 32000 },
    { occupied: 40000, free: 60000 },
    { occupied: 80000, free: 20000 },
    { occupied: 45000, free: 55000 },
    { occupied: 80000, free: 20000 },
  ],
  capacityMax: 100000,
};

/**
 * Impact corridors per event, keyed by event id. Stop names and line ids are
 * real (from analysis/network.ts, in timetable order) so the mini map matches
 * the network; the levels and peakPct are hand-set demo values, not computed.
 *
 * Each corridor runs 3-5 stops either side of the venue stop. Levels decay
 * outward from the venue, except where an interchange picks up transfer load.
 */
const IMPACT: Record<number, ImpactCorridor> = {
  // Schleyerhalle — venue stop Neckarpark, Bad Cannstatt as the interchange
  // absorbing the transfer load.
  1: { line: 'S1', peakPct: 78, stops: [
    { name: 'Stuttgart Feuersee', level: 'low' },
    { name: 'Stuttgart Stadtmitte', level: 'medium' },
    { name: 'Stuttgart Hbf (tief)', level: 'medium' },
    { name: 'Stuttgart-Bad Cannstatt', level: 'high' },
    { name: 'Stuttgart Neckarpark', level: 'high', target: true },
    { name: 'Stuttgart-Untertürkheim', level: 'medium' },
    { name: 'Stuttgart-Obertürkheim', level: 'low' },
    { name: 'Esslingen-Mettingen', level: 'low' },
    { name: 'Esslingen(Neckar)', level: 'low' },
  ]},
  // Stadium: the classic case — saturation at the venue stop and the interchange.
  2: { line: 'S1', peakPct: 96, stops: [
    { name: 'Stuttgart Universität', level: 'low' },
    { name: 'Stuttgart Schwabstr.', level: 'medium' },
    { name: 'Stuttgart Feuersee', level: 'medium' },
    { name: 'Stuttgart Stadtmitte', level: 'high' },
    { name: 'Stuttgart Hbf (tief)', level: 'high' },
    { name: 'Stuttgart-Bad Cannstatt', level: 'high' },
    { name: 'Stuttgart Neckarpark', level: 'high', target: true },
    { name: 'Stuttgart-Untertürkheim', level: 'medium' },
    { name: 'Stuttgart-Obertürkheim', level: 'low' },
    { name: 'Esslingen-Mettingen', level: 'low' },
  ]},
  // Wasen — sustained rather than peaked, spread over the Cannstatt approaches.
  3: { line: 'S2', peakPct: 84, stops: [
    { name: 'Waiblingen', level: 'low' },
    { name: 'Fellbach', level: 'medium' },
    { name: 'Stuttgart-Sommerrain', level: 'medium' },
    { name: 'Stuttgart Nürnberger Str.', level: 'high' },
    { name: 'Stuttgart-Bad Cannstatt', level: 'high', target: true },
    { name: 'Stuttgart Hbf (tief)', level: 'high' },
    { name: 'Stuttgart Stadtmitte', level: 'medium' },
    { name: 'Stuttgart Feuersee', level: 'low' },
    { name: 'Stuttgart Schwabstr.', level: 'low' },
  ]},
  4: { line: 'S1', peakPct: 54, stops: [
    { name: 'Stuttgart Feuersee', level: 'low' },
    { name: 'Stuttgart Stadtmitte', level: 'low' },
    { name: 'Stuttgart Hbf (tief)', level: 'medium' },
    { name: 'Stuttgart-Bad Cannstatt', level: 'medium' },
    { name: 'Stuttgart Neckarpark', level: 'high', target: true },
    { name: 'Stuttgart-Untertürkheim', level: 'low' },
    { name: 'Stuttgart-Obertürkheim', level: 'low' },
    { name: 'Esslingen-Mettingen', level: 'low' },
  ]},
  5: { line: 'S1', peakPct: 41, stops: [
    { name: 'Stuttgart-Österfeld', level: 'low' },
    { name: 'Stuttgart Universität', level: 'low' },
    { name: 'Stuttgart Schwabstr.', level: 'medium' },
    { name: 'Stuttgart Feuersee', level: 'medium' },
    { name: 'Stuttgart Stadtmitte', level: 'medium', target: true },
    { name: 'Stuttgart Hbf (tief)', level: 'medium' },
    { name: 'Stuttgart-Bad Cannstatt', level: 'low' },
    { name: 'Stuttgart Neckarpark', level: 'low' },
  ]},
  6: { line: 'S2', peakPct: 38, stops: [
    { name: 'Stuttgart Feuersee', level: 'low' },
    { name: 'Stuttgart Schwabstr.', level: 'low' },
    { name: 'Stuttgart Universität', level: 'low' },
    { name: 'Stuttgart-Österfeld', level: 'medium' },
    { name: 'Stuttgart-Vaihingen', level: 'medium', target: true },
    { name: 'Stuttgart-Rohr', level: 'medium' },
    { name: 'Oberaichen', level: 'low' },
    { name: 'Leinfelden', level: 'low' },
  ]},
  7: { line: 'S4', peakPct: 35, stops: [
    { name: 'Stuttgart Feuersee', level: 'low' },
    { name: 'Stuttgart Stadtmitte', level: 'low' },
    { name: 'Stuttgart Hbf (tief)', level: 'medium' },
    { name: 'Stuttgart Nordbahnhof', level: 'medium' },
    { name: 'Stuttgart-Feuerbach', level: 'medium', target: true },
    { name: 'Stuttgart-Zuffenhausen', level: 'low' },
    { name: 'Kornwestheim Pbf', level: 'low' },
    { name: 'Ludwigsburg', level: 'low' },
  ]},
  8: { line: 'S1', peakPct: 29, stops: [
    { name: 'Stuttgart Universität', level: 'low' },
    { name: 'Stuttgart Schwabstr.', level: 'low' },
    { name: 'Stuttgart Feuersee', level: 'medium' },
    { name: 'Stuttgart Stadtmitte', level: 'medium', target: true },
    { name: 'Stuttgart Hbf (tief)', level: 'low' },
    { name: 'Stuttgart-Bad Cannstatt', level: 'low' },
    { name: 'Stuttgart Neckarpark', level: 'low' },
  ]},
  // Peripheral case: no interchange spike, just moderately fuller trains out
  // along the Ludwigsburg branch.
  9: { line: 'S4', peakPct: 32, stops: [
    { name: 'Stuttgart Nordbahnhof', level: 'low' },
    { name: 'Stuttgart-Feuerbach', level: 'low' },
    { name: 'Stuttgart-Zuffenhausen', level: 'low' },
    { name: 'Kornwestheim Pbf', level: 'medium' },
    { name: 'Ludwigsburg', level: 'medium', target: true },
    { name: 'Favoritepark', level: 'medium' },
    { name: 'Freiberg(Neckar)', level: 'low' },
    { name: 'Benningen(Neckar)', level: 'low' },
    { name: 'Marbach(Neckar)', level: 'low' },
  ]},
  10: { line: 'S2', peakPct: 27, stops: [
    { name: 'Endersbach', level: 'low' },
    { name: 'Stetten-Beinstein', level: 'low' },
    { name: 'Rommelshausen', level: 'low' },
    { name: 'Waiblingen', level: 'medium' },
    { name: 'Fellbach', level: 'medium', target: true },
    { name: 'Stuttgart-Sommerrain', level: 'medium' },
    { name: 'Stuttgart Nürnberger Str.', level: 'low' },
    { name: 'Stuttgart-Bad Cannstatt', level: 'low' },
  ]},
  11: { line: 'S2', peakPct: 18, stops: [
    { name: 'Stuttgart-Österfeld', level: 'low' },
    { name: 'Stuttgart-Vaihingen', level: 'low' },
    { name: 'Stuttgart-Rohr', level: 'low' },
    { name: 'Oberaichen', level: 'medium' },
    { name: 'Leinfelden', level: 'medium', target: true },
    { name: 'Echterdingen', level: 'low' },
    { name: 'Stuttgart Flughafen/Messe', level: 'low' },
    { name: 'Filderstadt', level: 'low' },
  ]},
};

const cardSx = {
  backgroundColor: P.cardBg,
  border: `1px solid ${P.cardBorder}`,
  borderRadius: '12px',
  p: 2,
};

const LEVEL_COLOUR: Record<Level, string> = {
  high:   P.negative,
  medium: P.series.fire,
  low:    P.textMuted,
};

/** Heat ramp for the corridor map: red / amber / green. */
const HEAT: Record<Level, string> = {
  high:   '#c62828',
  medium: '#e8892b',
  low:    '#6f9a3f',
};

/** Drop the "Stuttgart" prefix and the Hbf platform qualifier for the labels. */
const shortStop = (n: string) => n
  .replace('Stuttgart-', '')
  .replace('Stuttgart ', '')
  .replace(' (tief)', '')
  .trim();

/**
 * Mini corridor map in the Stuttgart network-map idiom: a coloured line running
 * horizontally with a single 45° dogleg, white station discs with a dark ring,
 * and labels set at 45° — above the line before the bend, below it after.
 *
 * The bend is placed at the venue stop, which both matches the house style and
 * separates the approach side from the onward side. Schematic, not geographic:
 * real coordinates for 8-10 stops would be unreadable at this size.
 */
const CorridorMap: React.FC<{ corridor: ImpactCorridor; caption: string }> = ({ corridor, caption }) => {
  const W = 340, H = 178, PAD = 20, DROP = 30, Y_TOP = 68;
  const Y_BOT = Y_TOP + DROP;
  const { stops } = corridor;
  const n = stops.length;

  const found = stops.findIndex(s => s.target);
  const bendAt = found > 0 ? found : Math.floor(n / 2);
  const step = (W - PAD * 2 - DROP) / (n - 1);

  const pos = stops.map((s, i) => i < bendAt
    ? { x: PAD + i * step,        y: Y_TOP }
    : { x: PAD + i * step + DROP, y: Y_BOT });

  const bendX = pos[bendAt].x - DROP;
  const d = `M ${PAD},${Y_TOP} L ${bendX},${Y_TOP} L ${pos[bendAt].x},${Y_BOT} L ${pos[n - 1].x},${Y_BOT}`;
  const colour = LINES[corridor.line]?.color ?? P.textMuted;

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={caption}
      sx={{ width: '100%', display: 'block' }}
    >
      <path d={d} fill="none" stroke={colour} strokeWidth={5}
            strokeLinecap="round" strokeLinejoin="round" />

      {stops.map((s, i) => {
        const { x, y } = pos[i];
        const above = i < bendAt;
        const halo = s.level === 'high' ? 12 : s.level === 'medium' ? 9.5 : 7;
        const ly = above ? y - 9 : y + 11;
        return (
          <g key={s.name}>
            <circle cx={x} cy={y} r={halo} fill={HEAT[s.level]} opacity={0.28} />
            <circle
              cx={x} cy={y} r={s.target ? 5.5 : 4}
              fill="#ffffff" stroke={HEAT[s.level]} strokeWidth={s.target ? 3 : 2.2}
            />
            <text
              x={x} y={ly}
              transform={`rotate(-45 ${x} ${ly})`}
              textAnchor={above ? 'start' : 'end'}
              fontSize={7.5}
              fontWeight={s.target ? 700 : 400}
              fill={s.target ? P.textStrong : P.textMuted}
            >
              {shortStop(s.name)}
            </text>
          </g>
        );
      })}
    </Box>
  );
};

/**
 * Hit-rate colour thresholds, matching AccuracyBox on /analysis/messages
 * (>=90 good, >=70 watch, below that poor) but in the light palette.
 */
const hitColour = (pct: number | null) =>
  pct === null ? P.textFaint : pct >= 90 ? P.positive : pct >= 70 ? '#8a5a00' : P.negative;

/** HIGH read as LOW is the failure that misleads passengers — own thresholds. */
const missColour = (pct: number | null) =>
  pct === null ? P.textFaint : pct >= 10 ? P.negative : pct >= 5 ? '#8a5a00' : P.textStrong;

const LevelChip: React.FC<{ level: Level; label: string }> = ({ level, label }) => (
  <Box component="span" sx={{
    fontSize: '10px', px: 0.9, py: '2px', borderRadius: '999px', whiteSpace: 'nowrap',
    color: LEVEL_COLOUR[level], border: `1px solid ${LEVEL_COLOUR[level]}`,
  }}>
    {label}
  </Box>
);

const Label: React.FC<{ children: React.ReactNode; colour?: string }> = ({ children, colour }) => (
  <Typography sx={{ fontSize: '11px', color: colour ?? P.textMuted, mb: '2px' }}>{children}</Typography>
);

const Value: React.FC<{ children: React.ReactNode; colour?: string }> = ({ children, colour }) => (
  <Typography sx={{
    fontSize: '20px', fontWeight: 500, lineHeight: 1.2,
    color: colour ?? P.textStrong, fontVariantNumeric: 'tabular-nums',
  }}>
    {children}
  </Typography>
);

const HomePageInner: React.FC = () => {
  const { t, locale } = useI18n();
  const [selectedId, setSelectedId] = useState<number>(DEMO.events[0].id);
  const [accLine, setAccLine] = useState<string>('all');

  // ONE listener for the whole page. Declared up here because the forecast card
  // below derives from it; the rest of the live wiring reads `homeLive` further
  // down. Calling useHomeLive() twice would open a second snapshot listener on
  // the same document for no benefit.
  const homeLive = useHomeLive();
  const homeLiveDoc = homeLive.doc;
  const [feedWindow, setFeedWindow] = useState<'hour' | 'day'>('day');
  const [pisWindow, setPisWindow] = useState<'hour' | 'day' | 'week'>('day');

  // Forecast quality, from the hourly rollup. THE LAST COMPLETE DAY, not today:
  // today is a partial day that grows as trains reach their target stops, so as
  // a headline it would drift all morning and read low. The quotable figure is
  // the finished one, and the caption names its date.
  const accDay = homeLiveDoc?.accuracy?.last_complete_day ?? null;
  const accByLine = accDay?.by_line ?? {};
  const accNodeDoc = accLine === 'all' ? accDay?.overall : accByLine[accLine];
  const accNode = accNodeDoc ? toAccuracyNode(accNodeDoc) : null;

  /** Null when the rollup has not published — the card then shows em dashes
   *  rather than anything invented. */
  const acc: AccuracyMetrics | null = accNode ? {
    horizons: ACC_HORIZONS.map(key => {
      const { rate } = primaryHitRate(accNode[key]);
      return { key, hit: rate === null ? null : rate * 100 };
    }),
    overall:     (() => { const { rate } = primaryHitRate(accNode.all); return rate === null ? null : rate * 100; })(),
    samples:     accNode.all?.n ?? null,
    hnMiss:      accNode.all?.high_to_low_miss_rate === null || accNode.all?.high_to_low_miss_rate === undefined
                   ? null : accNode.all.high_to_low_miss_rate * 100,
    nHigh:       accNode.all?.n_high_actual ?? null,
    maePersons:  accNode.all?.mae_persons ?? null,
  } : null;

  const selected = DEMO.events.find(e => e.id === selectedId);
  const corridor = IMPACT[selectedId];
  const corridorCaption = corridor
    ? corridor.stops.map(s => `${shortStop(s.name)}: ${t(`home.level.${s.level}`)}`).join(', ')
    : '';

  const num = (n: number) => new Intl.NumberFormat(locale).format(n);
  const pct = (n: number) => {
    const s = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })
      .format(Math.abs(n));
    return `${n > 0 ? '+' : n < 0 ? '−' : ''}${s}%`;
  };
  /** Unsigned — for rates where a leading + would be meaningless (error rates). */
  const rate = (n: number) => new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1, maximumFractionDigits: 1,
  }).format(n) + '%';
  /** Em dash rather than 0 — an absent figure and a zero are different claims. */
  const numOrDash = (v: number | null | undefined) =>
    typeof v === 'number' ? num(v) : '—';
  const rateOrDash = (v: number | null | undefined) =>
    typeof v === 'number' ? rate(v) : '—';
  const hitOrDash = (v: number | null | undefined) =>
    typeof v === 'number' ? `${v.toFixed(1)}%` : '—';

  const dayLabels = t('home.days').split(',');

  const capacityData = DEMO.capacity.map((d, i) => ({ ...d, day: dayLabels[i] ?? '' }));



  // Real data. Everything else on this page is still DEMO.
  const pax = homeLive.doc?.passengers_onboard ?? null;
  const activeJourneys = homeLive.doc?.active_journeys?.count ?? null;

  const n = (v: number | null | undefined) => (typeof v === 'number' ? v : 0);

  // Units shown in brackets beside the journey count: AFZ + WiFi +
  // estimated-for-missing-formation, per the agreed definition. Deliberately
  // EXCLUDES unmeasured_units (formation known, neither sensor reported) —
  // those carry no passengers and appear as their own column in the box below,
  // so the box only sums to this figure while that count is zero.
  const activeUnits = pax
    ? n(pax.afz_units) + n(pax.wifi_units) + n(pax.no_formation_uic_estimate)
    : null;

  const paxTotalLive = pax ? n(pax.total_estimate) : null;

  const trips = homeLive.doc?.trips_day ?? null;
  const tripsDay = trips ? n(trips.total_estimate) : null;

  // Rolling windows, in days. MUST match KPI_ROLLING_DAYS on the rollup VM —
  // a window the writer does not publish renders as an em dash rather than
  // disappearing, so a mismatch is visible instead of silently changing the
  // number of tiles.
  const ROLLING_WINDOWS = [7, 30];
  const rollingWindows = homeLive.doc?.trips_rolling?.windows ?? null;

  const vu = homeLive.doc?.vu_coverage ?? null;
  const vuWin = vu?.windows?.[pisWindow] ?? null;
  const pisRows = (vuWin?.lines ?? []).map(l => ({
    line: l.line ?? '?',
    published: n(l.published),
    stops: n(l.departed),
    failed: n(l.failed),
    untriggered: n(l.untriggered),
  }));
  const pisPublished = vuWin ? n(vuWin.published) : null;
  const pisStops = vuWin ? n(vuWin.departed) : null;
  const pisPct = vuWin?.published_pct ?? null;

  // Name the cause, not just the count. "1,558 failed — all missing formation"
  // says what to fix; 75.4% only says something is wrong. The wording follows
  // the data: `all` only when one reason covers every failure today.
  const vuTop = vu?.failure_reasons?.[0] ?? null;
  const vuReasonLabel = vuTop?.reason
    ? t(`home.vuReason.${vuTop.reason}`, {}) || vuTop.reason
    : null;
  const vuFailCaption = (() => {
    const failed = vuWin ? n(vuWin.failed) : 0;
    if (!failed) return null;
    const parts: string[] = [];
    if (vuReasonLabel && vuTop) {
      parts.push(n(vuTop.share) >= 100
        ? t('home.vuFailedAll', { n: num(failed), reason: vuReasonLabel })
        : t('home.vuFailedMostly', {
            n: num(failed), reason: vuReasonLabel, pct: Math.round(n(vuTop.share)),
          }));
    }
    const untriggered = vuWin ? n(vuWin.untriggered) : 0;
    if (untriggered) parts.push(t('home.vuUntriggered', { n: num(untriggered) }));
    return parts.join(' · ');
  })();

  // Feeds. `feedRows` is null only when the writer has published no feeds field
  // at all (rollup down, or an older method_version); an individual feed that
  // has gone silent still appears as a row with null counts, which is the state
  // this box exists to make visible.
  const feedRows = homeLive.doc?.feeds?.windows?.[feedWindow]?.feeds ?? null;

  const rollingTile = (days: number): Kpi => {
    const w = rollingWindows?.[`days_${days}`] ?? null;
    const total = w ? n(w.total_estimate) : null;
    const counted = total ? Math.round((100 * n(w?.afz_boardings)) / total) : null;
    const short = w && n(w.days_with_data) < n(w.days);
    return {
      label: '',
      sub: t('home.lastNDays', { n: days }),
      value: total,
      hint: [
        counted !== null ? t('home.countedShare', { pct: counted }) : null,
        short ? t('home.daysWithData', {
          have: n(w?.days_with_data), want: n(w?.days),
        }) : null,
      ].filter(Boolean).join(' · ') || undefined,
    };
  };

  // Share of the estimated total that is a direct infrared count. Replaces the
  // placeholder "AFZ coverage 62% of 148 trains" — both of those numbers were
  // invented for the design mock.
  const countedShare = paxTotalLive
    ? Math.round((100 * n(pax?.afz_passengers)) / paxTotalLive)
    : null;

  type Kpi = {
    label: string;
    sub: string;
    value: number | null;
    /** Renders a freshness dot beside the sub-label. */
    live?: boolean;
    stale?: boolean;
    updatedMs?: number | null;
    /** Rendered smaller and muted in brackets after the value. */
    secondary?: number | null;
    /** Appended to the hover title — what the number is made of. */
    hint?: string;
  };

  const kpis: Kpi[] = [
    { label: t('home.activeJourneys'), sub: t('home.liveUnits'),
      value: activeJourneys, secondary: activeUnits,
      live: true, stale: homeLive.stale, updatedMs: homeLive.updatedMs,
      hint: t('home.unitsComposition') },
    { label: t('home.pax'), sub: t('home.live'), value: paxTotalLive,
      live: true, stale: homeLive.stale, updatedMs: homeLive.updatedMs },
    // Heading moves here: tile 2 is people on board right now, tiles 3-6 are
    // cumulative boardings. One passenger changing twice is three trips.
    { label: t('home.trips'), sub: t('home.day'), value: tripsDay,
      hint: trips?.afz_unit_share != null
        ? t('home.tripsHint', { pct: Math.round(n(trips.afz_unit_share)) })
        : undefined },
    ...ROLLING_WINDOWS.map(rollingTile),
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: P.pageBg }}>
      <HomeNav />

      {/* KPI strip */}
      <Box sx={{
        backgroundColor: P.cardBg, borderBottom: `1px solid ${P.cardBorder}`,
        display: 'flex', gap: 2, px: { xs: 2, md: 4 }, py: 2,
      }}>
        <Typography sx={{
          writingMode: 'vertical-rl', transform: 'rotate(180deg)',
          fontSize: '11px', color: P.textFaint, alignSelf: 'center',
        }}>
          {t('home.total')}
        </Typography>

        <Box sx={{
          flex: 1, display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: `repeat(${kpis.length}, minmax(0, 1fr))` },
          gap: 1.5,
        }}>
          {kpis.map((k, i) => (
            <Box
              key={i}
              title={[
                k.hint,
                k.live && k.updatedMs
                  ? `${t('home.live')} · ${new Date(k.updatedMs).toLocaleTimeString(locale)}`
                  : null,
              ].filter(Boolean).join(' — ') || undefined}
              sx={{ borderRight: { md: i < kpis.length - 1 ? `1px solid ${P.cardBorder}` : 'none' }, pr: 1.5 }}
            >
              <Label>{k.label || '\u00a0'}</Label>
              {k.live ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mb: '2px' }}>
                  <Box sx={{
                    width: 6, height: 6, borderRadius: '999px', flexShrink: 0,
                    backgroundColor: k.stale ? P.textFaint : P.positive,
                  }} />
                  <Typography sx={{ fontSize: '11px', color: P.textMuted }}>{k.sub}</Typography>
                </Box>
              ) : (
                <Label>{k.sub}</Label>
              )}
              <Typography sx={{ fontSize: '17px', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                {k.value === null || k.value === undefined ? '—' : num(k.value)}
                {k.secondary !== null && k.secondary !== undefined && (
                  <Box component="span" sx={{
                    fontSize: '12px', fontWeight: 400, color: P.textMuted, ml: '5px',
                  }}>
                    ({num(k.secondary)})
                  </Box>
                )}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Kept as a design placeholder for a feature we want, but there is no
            data source for it yet. Greyed to a single flat tone — the colour
            coding is what made it read as live, so every element is forced to
            textFaint rather than dimmed with opacity, which looks like a
            rendering fault rather than a deliberate state. */}
        <Box
          title={t('home.demoNoData')}
          sx={{ borderLeft: `1px solid ${P.cardBorder}`, pl: 2, minWidth: 130 }}
        >
          <Label colour={P.textFaint}>{t('home.trend')}</Label>
          {DEMO.trend.map(row => (
            <Box key={row.year} sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              fontSize: '11px', color: P.textFaint,
            }}>
              <TrainOutlinedIcon sx={{ fontSize: 14, color: P.textFaint }} />
              <Typography sx={{ fontSize: '11px', color: P.textFaint, fontVariantNumeric: 'tabular-nums' }}>
                {pct(row.pct)}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: P.textFaint }}>{pct(row.delta)}</Typography>
              <Typography sx={{ fontSize: '11px', color: P.textFaint }}>{row.year}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 4 }, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* Row 1 — current passengers, published messages, forecast quality */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
          <Box sx={cardSx}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>
              {t('home.currentPassengers')}
            </Typography>

            <Label>{t('home.estimatedTotal')}</Label>
            <Typography sx={{
              fontSize: '26px', fontWeight: 500, lineHeight: 1.15,
              fontVariantNumeric: 'tabular-nums',
            }}>
              {paxTotalLive === null ? '—' : num(paxTotalLive)}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: P.textFaint, mt: '2px' }}>
              {countedShare === null ? '\u00a0' : [
                t('home.countedShare', { pct: countedShare }),
                n(pax?.journeys_no_formation) > 0
                  ? t('home.journeysEstimated', { n: num(n(pax?.journeys_no_formation)) })
                  : null,
              ].filter(Boolean).join(' · ')}
            </Typography>

            {/* Tiers, in descending order of trust. The third is a double
                estimate — unit count AND load are modelled — so it is muted and
                labelled rather than folded into the total silently. The fourth
                appears only when non-zero: those units carry no passengers in
                the total, so a rising count means the headline understates. */}
            <Box sx={{ display: 'flex', gap: 2, mt: 1.5, pt: 1.25, borderTop: `1px solid ${P.cardBorder}` }}>
              <Box sx={{ flex: 1 }}>
                <Label>{t('home.afzMeasured')}</Label>
                <Typography sx={{ fontSize: '17px', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                  {pax ? num(n(pax.afz_passengers)) : '—'}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: P.textFaint }}>
                  {pax ? t('home.unitsN', { n: num(n(pax.afz_units)) }) : '\u00a0'}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Label>{t('home.wifiEstimate')}</Label>
                <Typography sx={{
                  fontSize: '17px', fontWeight: 500, color: P.textMuted,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {pax ? num(n(pax.wifi_passenger_estimate)) : '—'}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: P.textFaint }}>
                  {pax ? t('home.unitsN', { n: num(n(pax.wifi_units)) }) : '\u00a0'}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Label>{t('home.missingFormation')}</Label>
                <Typography sx={{
                  fontSize: '17px', fontWeight: 500, color: P.textMuted,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {pax ? num(n(pax.no_formation_passenger_estimate)) : '—'}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: P.textFaint }}>
                  {pax
                    ? `${t('home.unitsEstN', { n: num(n(pax.no_formation_uic_estimate)) })} · ` +
                      t('home.journeysEstimated', { n: num(n(pax.journeys_no_formation)) })
                    : '\u00a0'}
                </Typography>
              </Box>
              {n(pax?.unmeasured_units) > 0 && (
                <Box sx={{ flex: 1 }}>
                  <Label colour={P.negative}>{t('home.noReading')}</Label>
                  <Typography sx={{
                    fontSize: '17px', fontWeight: 500, color: P.textFaint,
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    0
                  </Typography>
                  <Typography sx={{ fontSize: '10px', color: P.negative }}>
                    {t('home.unitsN', { n: num(n(pax?.unmeasured_units)) })}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Published capacity messages — PIS coverage against stops served. */}
          <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 1, mb: 1, flexWrap: 'wrap',
            }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                {t('home.publishedMessages')}
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={pisWindow}
                onChange={(_e, v) => { if (v) setPisWindow(v); }}
                sx={{ '& .MuiToggleButton-root': { fontSize: '10px', py: 0.25, px: 1, textTransform: 'none' } }}
              >
                <ToggleButton value="hour">{t('home.lastHour')}</ToggleButton>
                <ToggleButton value="day">{t('home.currentDay')}</ToggleButton>
                <ToggleButton value="week">{t('home.last7')}</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Label>{t('home.published')}</Label>
                <Typography sx={{
                  fontSize: '26px', fontWeight: 500, lineHeight: 1.15,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {pisPublished === null ? '—' : num(pisPublished)}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: P.textFaint, mt: '2px' }}>
                  {pisStops === null ? '\u00a0' : t('home.vuDeparted', { n: num(pisStops) })}
                </Typography>
              </Box>
              <Box>
                <Label>{t('home.coverage')}</Label>
                <Typography sx={{
                  fontSize: '26px', fontWeight: 500, lineHeight: 1.15,
                  color: pisPct === null ? P.textFaint : hitColour(pisPct),
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {pisPct === null ? '—' : rate(pisPct)}
                </Typography>
              </Box>
            </Box>

            {vuFailCaption && (
              <Typography sx={{ fontSize: '11px', color: P.negative, mt: 1 }}>
                {vuFailCaption}
              </Typography>
            )}

            {/* Per-line coverage — the point of the tile: feed gaps hit lines
                differentially, so the headline number hides the problem. */}
            <Box sx={{
              mt: 'auto', pt: 1.5, borderTop: `1px solid ${P.cardBorder}`,
              display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 1,
            }}>
              {pisRows.map(l => {
                const p = (l.published / l.stops) * 100;
                return (
                  <Box
                    key={l.line}
                    title={`${l.line}: ${num(l.published)} / ${num(l.stops)}`
                      + (l.failed ? ` · ${num(l.failed)} failed` : '')
                      + (l.untriggered ? ` · ${num(l.untriggered)} never triggered` : '')}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '10px', color: P.textMuted }}>{l.line}</Typography>
                      <Typography sx={{
                        fontSize: '10px', fontWeight: 600, color: hitColour(p),
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        {Math.round(p)}%
                      </Typography>
                    </Box>
                    <Box sx={{
                      mt: '2px', height: 3, borderRadius: '2px',
                      backgroundColor: P.cardBorder,
                    }}>
                      <Box sx={{
                        width: `${p}%`, height: '100%', borderRadius: '2px',
                        backgroundColor: hitColour(p),
                      }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Forecast quality — summary of AccuracyBox on /analysis/messages. */}
          <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>{t('acc.title')}</Typography>

            <Select
              size="small"
              value={accLine}
              onChange={(e) => setAccLine(String(e.target.value))}
              sx={{ width: '100%', fontSize: '12px', mb: 1.5 }}
            >
              <MenuItem value="all" sx={{ fontSize: '12px' }}>{t('acc.allLines')}</MenuItem>
              {Object.keys(accByLine).map(id => (
                <MenuItem key={id} value={id} sx={{ fontSize: '12px' }}>
                  {t('acc.lineLabel', { line: id })}
                </MenuItem>
              ))}
            </Select>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
              {(acc?.horizons ?? ACC_HORIZONS.map(key => ({ key, hit: null }))).map(h => (
                <Box key={h.key} sx={{
                  flex: '1 1 auto', minWidth: 46, textAlign: 'center',
                  border: `1px solid ${P.cardBorder}`, borderRadius: '4px', py: 0.5,
                }}>
                  <Typography sx={{ fontSize: '9px', color: P.textMuted, letterSpacing: '0.05em' }}>
                    {h.key}
                  </Typography>
                  <Typography sx={{
                    fontSize: '15px', fontWeight: 600, lineHeight: 1.3,
                    color: hitColour(h.hit), fontVariantNumeric: 'tabular-nums',
                  }}>
                    {hitOrDash(h.hit)}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography sx={{ fontSize: '12px', color: P.textMuted }}>
              {t('acc.all')}:{' '}
              <Box component="span" sx={{ fontSize: '15px', fontWeight: 600, color: hitColour(acc?.overall ?? null) }}>
                {hitOrDash(acc?.overall)}
              </Box>{' '}
              <Box component="span" sx={{ fontSize: '10px' }}>({t('acc.modelClass')})</Box>
            </Typography>
            <Typography sx={{ fontSize: '10px', color: P.textFaint }}>
              {t('acc.samples', { n: numOrDash(acc?.samples) })}
            </Typography>
            {/* Which day this is. The figure is the LAST COMPLETE day, not
                today — naming it is what stops it being read as live. */}
            <Typography sx={{ fontSize: '10px', color: P.textFaint }}>
              {accDay?.day
                ? `Abgeschlossener Tag ${accDay.day}`
                : 'Noch keine Daten aus dem stündlichen Rollup'}
              {accDay?.model_source && accDay.model_source !== 'forecaster_archive'
                && ' · Modellklassen nicht verfügbar'}
            </Typography>

            <Box sx={{ mt: 'auto', pt: 1.5, borderTop: `1px solid ${P.cardBorder}` }}>
              <Typography sx={{ fontSize: '11px', color: P.textMuted, mb: 0.25 }}>
                {t('acc.hnError')}:{' '}
                <Box component="span" sx={{ fontWeight: 600, color: missColour(acc?.hnMiss ?? null) }}>
                  {rateOrDash(acc?.hnMiss)}
                </Box>{' '}
                {t('acc.ofHigh', { n: numOrDash(acc?.nHigh) })}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: P.textMuted }}>
                {t('acc.mae')}:{' '}
                <Box component="span" sx={{ fontWeight: 600, color: P.textStrong }}>
                  {numOrDash(acc?.maePersons)} {t('acc.persons')}
                </Box>
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Row 2 — events (detector + impact as one card) and feeds.

            The list and the panel are a single master/detail unit: the list
            selects, the panel explains the selection. They used to be two
            separate cards with the event list duplicated into a dropdown, which
            read as two unrelated widgets that happened to sit side by side.
            The rows are now the selector and the dropdown is gone — one list,
            one selection, one card. */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 300px' },
          gap: 2,
        }}>
          <Box sx={{
            ...cardSx, p: 0, overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1fr)' },
          }}>
            {/* Left half — the detector list, which is also the selector. */}
            <Box sx={{ p: 2, minWidth: 0 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 1 }}>{t('home.eventDetector')}</Typography>

              <Box sx={{
                display: 'flex', gap: 1, pb: 0.5, borderBottom: `1px solid ${P.cardBorder}`,
              }}>
                <Typography sx={{ fontSize: '10px', color: P.textFaint, flex: 1 }}>{t('home.event')}</Typography>
                <Typography sx={{ fontSize: '10px', color: P.textFaint }}>{t('home.impactConfidence')}</Typography>
              </Box>

              {/* Scrollable: the detector is expected to return more rows than fit. */}
              <Box sx={{ maxHeight: 236, overflowY: 'auto' }}>
                {DEMO.events.map((e, i) => {
                  const isSel = e.id === selectedId;
                  return (
                    <Box
                      key={e.id}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isSel}
                      onClick={() => setSelectedId(e.id)}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter' || ev.key === ' ') {
                          ev.preventDefault();
                          setSelectedId(e.id);
                        }
                      }}
                      sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.9,
                        borderTop: i ? `1px solid ${P.cardBorder}` : 'none',
                        cursor: 'pointer',
                        // Negative margin pulls the accent bar out to the card's
                        // inner edge so it reads as a marker on the row rather
                        // than an indent inside it.
                        pl: 1, ml: -1,
                        borderLeft: `3px solid ${isSel ? HEAT[e.impact] : 'transparent'}`,
                        backgroundColor: isSel ? P.pageBg : 'transparent',
                        '&:hover': { backgroundColor: P.pageBg },
                        '&:focus-visible': {
                          outline: `2px solid ${P.textStrong}`, outlineOffset: '-2px',
                        },
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>{e.name}</Typography>
                        <Typography sx={{ fontSize: '11px', color: P.textMuted }}>
                          {e.venue} · {e.when}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        <LevelChip level={e.impact} label={t(`home.level.${e.impact}`)} />
                        <LevelChip level={e.confidence} label={t(`home.level.${e.confidence}`)} />
                        {/* Points across at the panel describing this row. Hidden
                            when the halves stack, where "across" means nothing.
                            Always rendered so the row width never shifts. */}
                        <Typography sx={{
                          display: { xs: 'none', sm: 'block' },
                          fontSize: '13px', lineHeight: 1, fontWeight: 600,
                          color: isSel ? P.textStrong : 'transparent',
                        }}>
                          ›
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Right half — the detail for whatever is selected on the left.
                Divider becomes a top border once the halves stack. */}
            <Box sx={{
              p: 2, minWidth: 0, display: 'flex', flexDirection: 'column',
              borderLeft: { xs: 'none', sm: `1px solid ${P.cardBorder}` },
              borderTop: { xs: `1px solid ${P.cardBorder}`, sm: 'none' },
            }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{t('home.impactAnalysis')}</Typography>
              {/* Naming the event is what closes the loop: the panel states which
                  row it describes instead of leaving that to be inferred from a
                  highlight halfway across the card. minHeight holds the line so
                  changing selection does not shift the layout. */}
              <Typography sx={{
                fontSize: '11px', color: P.textMuted, mb: 1, minHeight: 17,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {selected ? selected.name : ''}
              </Typography>

              {selected && corridor && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: '11px', color: P.textMuted, flex: 1, minWidth: 0 }}>
                      {corridor.line} · {selected.venue}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: HEAT.high, whiteSpace: 'nowrap' }}>
                      {t('home.peak')} +{corridor.peakPct}%
                    </Typography>
                  </Box>

                  <CorridorMap corridor={corridor} caption={corridorCaption} />
                </>
              )}

              {/* Legend pinned to the bottom edge of the card. */}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 'auto', pt: 1.5 }}>
                {(['high', 'medium', 'low'] as Level[]).map(lv => (
                  <Box key={lv} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: HEAT[lv] }} />
                    <Typography sx={{ fontSize: '10px', color: P.textMuted }}>{t(`home.level.${lv}`)}</Typography>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{
                    width: 9, height: 9, borderRadius: '50%',
                    border: `2px solid ${P.textStrong}`,
                  }} />
                  <Typography sx={{ fontSize: '10px', color: P.textMuted }}>{t('home.targetStop')}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Feeds — raw ingest volume and processing failures per source. */}
          <Box sx={cardSx}>
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 1, mb: 1, flexWrap: 'wrap',
            }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{t('home.feeds')}</Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={feedWindow}
                onChange={(_e, v) => { if (v) setFeedWindow(v); }}
                sx={{ '& .MuiToggleButton-root': { fontSize: '10px', py: 0.25, px: 1, textTransform: 'none' } }}
              >
                <ToggleButton value="hour">{t('home.lastHour')}</ToggleButton>
                <ToggleButton value="day">{t('home.currentDay')}</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto auto auto',
              columnGap: 1.5, alignItems: 'baseline',
            }}>
              <Typography sx={{ fontSize: '10px', color: P.textFaint }}>&nbsp;</Typography>
              <Typography sx={{ fontSize: '10px', color: P.textFaint, textAlign: 'right' }}>
                {t('home.received')}
              </Typography>
              <Typography sx={{ fontSize: '10px', color: P.textFaint, textAlign: 'right' }}>
                {t('home.feedPublished')}
              </Typography>
              <Typography sx={{ fontSize: '10px', color: P.textFaint, textAlign: 'right' }}>
                {t('home.feedFiltered')}
              </Typography>

              {(feedRows ?? []).map(f => {
                const cell = {
                  fontSize: '12px', py: 0.6, textAlign: 'right' as const,
                  fontVariantNumeric: 'tabular-nums' as const,
                  borderTop: `1px solid ${P.cardBorder}`,
                };
                // Silent feed. Null is not zero, and rendering it as 0 would
                // claim we measured nothing arriving rather than admitting we
                // heard nothing at all.
                if (f.received == null) {
                  return (
                    <React.Fragment key={f.name}>
                      <Typography sx={{
                        fontSize: '12px', fontWeight: 500, py: 0.6,
                        borderTop: `1px solid ${P.cardBorder}`,
                      }}>
                        {f.name}
                      </Typography>
                      <Typography sx={{
                        ...cell, gridColumn: 'span 3',
                        color: P.negative, fontWeight: 600,
                      }}>
                        {t('home.feedNoData')}
                      </Typography>
                    </React.Fragment>
                  );
                }
                const errs = n(f.errors);
                return (
                  <React.Fragment key={f.name}>
                    <Box sx={{ py: 0.6, borderTop: `1px solid ${P.cardBorder}`, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                        {f.name}
                      </Typography>
                      {/* Errors get a line only when there are some. FzO-BO has
                          had zero across 60M messages, so a permanently empty
                          column would be pure noise. */}
                      {errs > 0 && (
                        <Typography sx={{ fontSize: '10px', color: P.negative, fontWeight: 600 }}>
                          {t('home.feedErrors', { n: num(errs) })}
                        </Typography>
                      )}
                    </Box>
                    <Typography sx={{ ...cell, fontWeight: 600 }}>
                      {num(n(f.received))}
                    </Typography>
                    <Typography sx={{ ...cell }}>
                      {num(n(f.published))}
                    </Typography>
                    {/* Muted, not red: these are messages the listener
                        discarded on purpose, not failures. The breakdown rides
                        along as a hover so the headline stays one number. */}
                    <Typography
                      sx={{ ...cell, color: P.textMuted }}
                      title={(f.drop_reasons ?? [])
                        .map(r => `${r.reason}: ${num(n(r.count))}`)
                        .join('\n')}
                    >
                      {num(n(f.dropped))}
                    </Typography>
                  </React.Fragment>
                );
              })}

              {/* Not yet instrumented. Grey throughout, and no Published or
                  Filtered figures invented for them — an em dash is honest
                  where a number would not be. */}
              {DEMO.feedPlaceholders[feedWindow].map(f => (
                <React.Fragment key={f.name}>
                  <Box sx={{ py: 0.6, borderTop: `1px solid ${P.cardBorder}`, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: P.textFaint }}>
                      {f.name}
                    </Typography>
                    {f.errors > 0 && (
                      <Typography sx={{ fontSize: '10px', color: P.textFaint }}>
                        {t('home.feedErrors', { n: num(f.errors) })}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{
                    fontSize: '12px', py: 0.6, textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums', color: P.textFaint,
                    borderTop: `1px solid ${P.cardBorder}`,
                  }}>
                    {num(f.received)}
                  </Typography>
                  <Typography sx={{
                    fontSize: '12px', py: 0.6, textAlign: 'right', color: P.textFaint,
                    borderTop: `1px solid ${P.cardBorder}`,
                  }}>
                    —
                  </Typography>
                  <Typography sx={{
                    fontSize: '12px', py: 0.6, textAlign: 'right', color: P.textFaint,
                    borderTop: `1px solid ${P.cardBorder}`,
                  }}>
                    —
                  </Typography>
                </React.Fragment>
              ))}
            </Box>

            {feedRows === null && (
              <Typography sx={{ fontSize: '10px', color: P.textFaint, mt: 1 }}>
                {t('home.demoNoData')}
              </Typography>
            )}
            <Typography sx={{ fontSize: '10px', color: P.textFaint, mt: 1, lineHeight: 1.5 }}>
              {t('home.feedFilteredNote')}<br />
              {t('home.feedPlaceholderNote')}
            </Typography>
          </Box>
        </Box>

        {/* Row 3 — capacity summary */}
        <Box sx={cardSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
              {t('home.summary')}{' '}
              <Box component="span" sx={{ color: P.accentCard }}>· {t('home.capacity')}</Box>
            </Typography>
            <Typography sx={{ fontSize: '11px', color: P.textMuted }}>{t('home.last7')}</Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
            {[
              { label: t('home.occupancy'), fill: P.series.occupied },
              { label: t('home.freeCapacity'), fill: P.series.free },
            ].map(l => (
              <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '2px', backgroundColor: l.fill }} />
                <Typography sx={{ fontSize: '11px', color: P.textMuted }}>{l.label}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ height: 210 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={capacityData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={P.cardBorder} vertical={false} />
                <XAxis dataKey="day" tickLine={false} axisLine={false}
                       tick={{ fontSize: 11, fill: P.textMuted }} />
                <YAxis domain={[0, DEMO.capacityMax]} tickLine={false} axisLine={false}
                       tick={{ fontSize: 11, fill: P.textMuted }}
                       tickFormatter={(v: number) => `${Math.round(v / 1000)}k`} />
                <Bar dataKey="occupied" stackId="cap" fill={P.series.occupied} barSize={16} />
                <Bar dataKey="free" stackId="cap" fill={P.series.free} barSize={16} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const HomePage: React.FC = () => (
  <I18nProvider>
    <HomePageInner />
  </I18nProvider>
);

export default HomePage;
